import React, { useContext, useRef } from 'react'
import Button from '../../components/button/button'
import DateSelector from '../../components/date-selector'
import MonthSelector from '../../components/month-selector'
import DetailsSections from '../../components/details-sections'
import TimeSelector from '../../components/time-selector'
import Divider from '../../components/divider'
import { useAppointmentDetails, useTranslation } from '../../hooks'
import useBook from './useBook'
import dayjs from 'dayjs'
import './_book.scss'
import { useLocation } from 'react-router'
import Alert from '../../components/alert/alert'
import SmsForm from '../../components/smsForm'
import { AppContext, ServicesContext } from '../../contexts'

const Book = () => {
  const { i18n } = useTranslation();
  const { state } = useLocation();
  const {
    setCurrentWeek,
    setCurrentMonth,
    selectedDay,
    daysWithAvailableTimeSlots,
    appointmentsLoading,
    setSelectedDay,
    selectedDayTimeSlots,
    setSelectedTimeSlot,
    isValid,
    isSubmitting,
    handleBookingConfirm,
    handleRescheduleCancel,
    errorMessage,
    setErrorMessage,
    loadingDatesMonthView,
    setLoadingDatesMonthView,
    setTimeSlots,
    selectedTimeSlot
  } = useBook();
  const details = useAppointmentDetails();
  const { appointmentData, smsConsentDetails } = useContext(AppContext);
  const { saveSmsConsent } = useContext(ServicesContext);
  const { isSmsConsentRequired, siteTimezoneId, isMonthViewEnabled, currentSmsConsent } = appointmentData;
  const { current: smsConsentBlock } = useRef('sms_consent');
  const smsConsentForm = smsConsentDetails?.[smsConsentBlock];
  const smsFormRef = useRef(null);

  const handleWeekChange = date => setCurrentWeek(dayjs(date).format('YYYY-MM-DD'));
  const handleMonthChange = date => {setTimeSlots([]); setLoadingDatesMonthView(0); setCurrentMonth(dayjs(date).format('YYYY-MM-DD'))};

  const handleSubmitClick = async () => {
    if (isSmsConsentRequired && smsFormRef.current) {
      await smsFormRef.current.submit();
    }
    setErrorMessage("");
    await handleBookingConfirm();
  }
  const handleClose = () => setErrorMessage("");

  const handleSmsConsent = async fields => {
    const submittedSmsConsent = {
      [smsConsentBlock]: {
        ...smsConsentForm,
        fields
      }
    };
    (async () => await saveSmsConsent(submittedSmsConsent))();
  }

  const submit = (
    <Button
      testId='submit'
      loading={isSubmitting}
      disabled={!isValid}
      onClick={handleSubmitClick}
      form={smsConsentBlock}
    >
      {i18n('cp_confirm_and_submit')}
    </Button>
  );

  const smsConsentFields = smsConsentForm?.fields?.map((field) => {
    if (field.component === 'switch') {
      return {
        ...field,
        options: {
          ...field.options,
          value: currentSmsConsent
        }
      };
    }
    return field;
  });

  return (
    <div className='container card book'>
      <DetailsSections title={i18n('cp_appointment_details')} items={details} />
      {isSmsConsentRequired && (
        <SmsForm 
          fields={smsConsentFields} 
          onSubmit={handleSmsConsent} 
          formRef={smsFormRef}
        />
      )}
      <Divider />
      {!!errorMessage && <Alert label={errorMessage} onClose={handleClose} />}
      <div className='book__date-time-selectors'>
        {isMonthViewEnabled ? (
          <MonthSelector
          title={i18n('cp_select_date')}
          onMonthChange={handleMonthChange}
          enabledDays={daysWithAvailableTimeSlots}
          loading={appointmentsLoading}
          onSelect={setSelectedDay}
          loadingDatesMonthView={loadingDatesMonthView}
        />
         ) : (
          <DateSelector
          title={i18n('cp_select_date')}
          onWeekChange={handleWeekChange}
          enabledDays={daysWithAvailableTimeSlots}
          loading={appointmentsLoading}
          onSelect={setSelectedDay}
        />
        )}
        {selectedDay !== undefined && (
          <TimeSelector
            title={i18n('cp_available_time_slots')}
            timezone={siteTimezoneId}
            options={selectedDayTimeSlots}
            onChange={setSelectedTimeSlot}
            selectedTimeSlot={selectedTimeSlot}
          />
        )}
      </div>
      {state?.isReschedule ? (
        <div className='book__actions'>
          {submit}
          <Button
            testId='cancel'
            loading={isSubmitting}
            theme='outline'
            onClick={handleRescheduleCancel}
          >
            {i18n('cp_cancel')}
          </Button>
        </div>
      ) : (
        submit
      )}
    </div>
  );
}

export default Book
